import { ZodType, ZodTypeDef } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  Watchable,
} from '../../actions/framework';
import { ExpressionColumnModel } from '../expression/expression-column.model';
import { LlmColumnModel } from '../llm/llm-column.model';
import {
  AskResponse,
  ModelConstructor,
  TargetableModel,
} from '../../actions/framework/types';
import { GraphModel } from '../../graph/graph.model';
import { Assert } from '@cotera/utilities';
import { err, ok } from 'neverthrow';
import { COLUMN_ACTION_GROUP } from '../constants';
import { TaxonomyColumnModel } from '../taxonomy/taxonomy-column.model';
import { useState } from 'react';
import { ModalPageView, Loading } from '@cotera/client/app/components';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ToolCallModel } from '../tool-call/tool-call.model';
import { findColumnNameConflict } from '../utils/column-name-validation';
import { DG } from '@cotera/sdk/core';

type RenamePayload = {
  to: string;
};
type RenameError = { t: string };

type ColumnModel<FormState extends { name: string } = { name: string }> =
  TargetableModel & {
    datasetName: string;
    currentNameInGraph: Watchable<string>;
    formState: Watchable<FormState>;
  };

const COLUMN_MODEL_CLASSES: readonly ModelConstructor<ColumnModel<any>>[] = [
  ExpressionColumnModel,
  LlmColumnModel,
  TaxonomyColumnModel,
  ToolCallModel,
] as const;

export class RenameColumnAction extends BaseAction<
  RenamePayload,
  RenameError[]
> {
  override title: string = 'Rename Column';
  description: 'Renames a column.';
  override inputSchema: ZodType<RenamePayload, ZodTypeDef, RenamePayload>;
  group = COLUMN_ACTION_GROUP;
  icon = 'pencil';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    const targetedColumn = getTargetedColumn(context);
    return (
      targetedColumn !== null &&
      targetedColumn.currentNameInGraph.snapshot() !== null
    );
  }

  override async execute(
    payload: RenamePayload,
    context: ExecuteContext
  ): Promise<ActionResult<RenameError[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const column = getTargetedColumn(context);

    Assert.assert(graph !== null);
    Assert.assert(column !== null);

    const datasetName = column.datasetName;
    const oldColumnName = column.currentNameInGraph.snapshot();
    const newColumnName = payload.to;

    if (oldColumnName === newColumnName) {
      return ok({});
    }

    // Check for column name conflicts in current dataset and parent lineage
    // Exclude the current column since we're renaming it
    const conflictDataset = findColumnNameConflict(
      graph.graph.snapshot(),
      datasetName,
      newColumnName,
      true // Exclude current column since we're renaming it
    );

    if (conflictDataset !== null) {
      return err([
        {
          t: 'error',
          message: `A column with the name "${newColumnName}" already exists in dataset "${conflictDataset}"`,
        },
      ]);
    }

    column.formState.set({
      ...column.formState.snapshot(),
      name: newColumnName,
    });

    const columnBuildExplorer = graph.graph.snapshot().getColumn({
      rel: datasetName,
      name: oldColumnName,
    });

    Assert.assert(columnBuildExplorer !== null);
    const ops = columnBuildExplorer.makeRenameOps({
      to: newColumnName,
    });

    if (ops.isErr()) {
      return err([{ t: 'build', e: ops.error }]);
    }

    const result = await graph.attemptToStageOperations(ops.value);
    if (result.isErr()) {
      return err([{ t: 'build', e: result.error }]);
    }

    column.currentNameInGraph.set(newColumnName);

    return ok({});
  }

  ask(
    _: Partial<RenamePayload>,
    context: ExecuteContext
  ): AskResponse<RenamePayload> {
    const column = getTargetedColumn(context);
    const graph = context.getInScopeModelOfType(GraphModel);
    Assert.assert(column !== null);
    Assert.assert(graph !== null);

    const columnName = column.currentNameInGraph.snapshot();
    Assert.assert(columnName !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.pop();
                }}
                name={columnName}
                description={this.description}
                graph={graph.graph.snapshot()}
                datasetName={column.datasetName}
                currentColumnName={columnName}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

function getTargetedColumn(
  context: ApplicableContext | ExecuteContext
): ColumnModel | null {
  for (const ModelClass of COLUMN_MODEL_CLASSES) {
    const model = context.targetedModelOfType(
      ModelClass as ModelConstructor<ColumnModel<any>>
    );

    if (model !== null) {
      return model;
    }
  }

  return null;
}

const AskView: React.FC<{
  onRespond: (params: RenamePayload) => Promise<void>;
  name: string;
  description: string;
  graph: DG.BuildExplorer;
  datasetName: string;
  currentColumnName: string;
}> = ({
  onRespond,
  name,
  description,
  graph,
  datasetName,
  currentColumnName,
}) => {
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  const conflictDataset =
    to !== currentColumnName
      ? findColumnNameConflict(graph, datasetName, to, true)
      : null;

  const validName = conflictDataset === null && to.trim().length > 0;

  return (
    <ModalPageView
      title={`Rename ${name}`}
      subtitle={description}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Input
            autoFocus
            placeholder="name"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          {conflictDataset !== null && (
            <span className="text-xs text-destructive">
              A column with this name already exists in dataset "
              {conflictDataset}"
            </span>
          )}
        </div>
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            disabled={!validName || loading}
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              await onRespond({ to });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
